// src/components/enterprise/ConsoleSidebar.jsx
//
// THE CONSOLE'S OWN SIDEBAR. When an admin opens Workforce Analytics the app
// swaps its hub sidebar for this one (App.jsx), so the executive console is a
// page of its own: the organization's mark, the console tabs and nothing
// else, then the way back to the hub and to System Admin. Colours come from
// the edition palette the console already uses (editionVars), not the hub
// theme, so the console reads as one surface from edge to edge. Same drawer
// behaviour as the hub sidebar on small screens (isOpen / setIsOpen).

import React from 'react';
import { ArrowLeft, Settings, Shield, LogOut, Lock, X } from 'lucide-react';
import { EditionProvider, useEdition, editionVars } from '../../data/editions/EditionContext';
import { copyFor, isCorporateOrg } from '../../data/copyDictionary';
import { visibleConsoleTabs, consoleTabLabel, isDeferredTab, DEFERRED_TAB_NOTE } from './consoleTabs';

const linkCls = 'w-full flex items-center gap-3 px-4 py-2 rounded-xl text-sm font-semibold text-white/75 hover:bg-white/10 hover:text-white transition-colors text-left';

const ConsoleSidebarInner = ({ activeTab, navigateTo, isOpen, setIsOpen, currentUser, currentOrg, isSuperAdmin, onLogout }) => {
    const edition = useEdition();
    const isCorporate = isCorporateOrg(currentOrg);
    const t = (key) => copyFor(key, isCorporate);
    const branding = currentOrg?.branding || {};
    const logo = branding.enabled && branding.logoUrl ? branding.logoUrl : null;
    const name = (branding.enabled && branding.theme === 'hub' && branding.hubName) || currentOrg?.name || 'ERI HUB';
    const current = activeTab === 'command-center' ? 'cc-overview' : activeTab;
    const go = (id) => { navigateTo(id); if (setIsOpen) setIsOpen(false); };

    return (
        <>
            {isOpen && <div className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden" onClick={() => setIsOpen(false)} aria-hidden="true" />}
            <aside
                style={{ ...editionVars(edition), backgroundColor: 'var(--brand-deep)' }}
                className={`fixed lg:static inset-y-0 left-0 z-50 w-72 shrink-0 flex flex-col text-white transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 print:hidden`}
            >
                <div className="relative px-6 pt-5 pb-4 border-b border-white/10">
                    <div className="flex flex-col items-center text-center">
                        {logo
                            ? <img src={logo} alt="" className="h-14 w-auto max-w-[160px] object-contain rounded-xl mb-3" />
                            : <div className="text-2xl font-black italic tracking-tight mb-2">EAS</div>}
                        <p className="font-black uppercase tracking-wider leading-tight text-sm">{name}</p>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--brand-yellow)] mt-1">{edition.consoleLabel || t('navExecInsights')}</p>
                    </div>
                    <button type="button" className="lg:hidden absolute top-4 right-3 p-2 rounded-lg hover:bg-white/10" onClick={() => setIsOpen(false)} aria-label="Close menu"><X size={18} /></button>
                </div>

                <nav className="flex-1 overflow-y-auto no-scrollbar px-4 py-3 space-y-0.5">
                    {visibleConsoleTabs(edition, currentOrg).map((tab) => {
                        const active = current === tab.id;
                        const Icon = tab.Icon;
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => go(tab.id)}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-left text-sm font-semibold transition-colors border ${active ? 'bg-white text-[var(--brand-deep)] border-white shadow-md' : 'border-transparent text-white/80 hover:bg-white/10 hover:text-white'}`}
                            >
                                <Icon size={18} className={active ? 'text-[var(--brand-blue)]' : 'text-[var(--brand-yellow)]'} />
                                <span>{consoleTabLabel(tab, t)}</span>
                                {isDeferredTab(currentOrg, tab.id) && <span className={`ml-auto text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md whitespace-nowrap ${active ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-white/60'}`}>{DEFERRED_TAB_NOTE}</span>}
                            </button>
                        );
                    })}
                </nav>

                <div className="px-4 pb-4 pt-2 border-t border-white/10 space-y-0.5">
                    <button type="button" onClick={() => go('dashboard')} className={linkCls}><ArrowLeft size={16} /> Back to the hub</button>
                    <button type="button" onClick={() => go('admin')} className={linkCls}><Settings size={16} /> System Admin</button>
                    {isSuperAdmin && <button type="button" onClick={() => go('super-admin')} className={linkCls}><Shield size={16} /> Super Admin</button>}
                    <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 mt-1.5">
                        <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center shrink-0"><Lock size={15} /></div>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold truncate">{currentUser?.name || currentUser?.email || 'Executive'}</p>
                            <p className="text-[11px] text-white/60 truncate">{t('roleExec')} Mode</p>
                        </div>
                    </div>
                    <button type="button" onClick={onLogout} className="w-full flex items-center justify-center gap-2 px-4 py-1.5 rounded-xl text-sm font-medium text-white/60 hover:text-red-300 hover:bg-red-500/10 transition-colors"><LogOut size={16} /> Log Out</button>
                </div>
            </aside>
        </>
    );
};

// The edition comes from the org (EditionProvider), the same resolution the console uses.
const ConsoleSidebar = (props) => (
    <EditionProvider org={props.currentOrg}>
        <ConsoleSidebarInner {...props} />
    </EditionProvider>
);

export default ConsoleSidebar;
